import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import SEO from "@/components/SEO";
import { 
  Check, 
  ArrowRight, 
  Lock, 
  Wifi, 
  Coffee, 
  Users,
  Building,
  Clock
} from "lucide-react";

const images = {
  hero: "https://customer-assets.emergentagent.com/job_work-space-hub/artifacts/d6ok7fck_Interior%20Office%201.jpg",
  office1: "https://customer-assets.emergentagent.com/job_work-space-hub/artifacts/d6ok7fck_Interior%20Office%201.jpg",
  office2: "https://customer-assets.emergentagent.com/job_work-space-hub/artifacts/meesobcs_Interior%20Office%204.jpg",
  office3: "https://customer-assets.emergentagent.com/job_work-space-hub/artifacts/g87r23yx_Interior%20Office%203.jpg",
  office4: "https://customer-assets.emergentagent.com/job_work-space-hub/artifacts/6xq79mqc_Kitchen.jpg",
};

const features = [
  { icon: Lock, text: "24/7 Secure Access" },
  { icon: Wifi, text: "High-Speed WiFi" },
  { icon: Coffee, text: "Kitchen & Coffee Bar" },
  { icon: Users, text: "Conference Room Access" },
  { icon: Building, text: "Business Address" },
  { icon: Clock, text: "Flexible Terms" },
];

const benefits = [
  "Fully furnished with desk and chair",
  "Ocean views and natural lighting",
  "Private lockable space",
  "Mail handling and business address",
  "Access to conference room and common areas",
  "Networking opportunities with other members",
  "Restaurant and amenities on-site",
  "Free parking available",
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

export const OfficesPage = () => {
  return (
    <div className="pt-20" data-testid="offices-page">
      <SEO
        title="Private Office Rentals Satellite Beach & Indialantic FL | Coastal Collaborative"
        description="Fully-furnished private office rentals in Satellite Beach with ocean views. 16 private offices serving Indialantic, Melbourne Beach & Brevard County. Month-to-month or long-term leases."
        canonical="https://www.workcoastal.com/offices"
      />
      {/* Hero Section */}
      <section className="relative py-32 bg-brand overflow-hidden" data-testid="offices-hero">
        <div className="absolute inset-0 opacity-20">
          <img
            src={images.hero}
            alt="Private Office"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-2xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className="inline-block font-sans text-sm font-medium text-sea-light uppercase tracking-wider mb-4">
              Office Rentals Satellite Beach & Indialantic
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl font-bold text-white leading-tight mb-6">
              Private Office Rentals
              <span className="block text-sand-light">in Brevard County</span>
            </h1>
            <p className="font-sans text-lg text-white/80 leading-relaxed mb-8">
              Fully-furnished private offices with ocean views in Satellite Beach. 
              Serving Indialantic, Melbourne Beach, and all of Brevard County Florida. 
              Available month-to-month or long-term.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-sea hover:bg-sea-light text-white rounded-full px-8 btn-primary"
                data-testid="offices-hero-cta"
              >
                Schedule a Tour
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 bg-white border-b border-sand/30" data-testid="offices-features">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.text}
                className="text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
              >
                <div className="w-12 h-12 rounded-full bg-sand-light flex items-center justify-center mx-auto mb-3">
                  <feature.icon className="w-6 h-6 text-brand" strokeWidth={1.5} />
                </div>
                <p className="font-sans text-sm font-medium text-brand">{feature.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-white" data-testid="offices-content">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            {/* Gallery */}
            <motion.div
              className="grid grid-cols-2 gap-4"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src={images.office1}
                alt="Private Office 1"
                className="rounded-2xl image-hover w-full aspect-square object-cover"
              />
              <img
                src={images.office2}
                alt="Private Office 2"
                className="rounded-2xl image-hover w-full aspect-square object-cover mt-8"
              />
              <img
                src={images.office3}
                alt="Private Office 3"
                className="rounded-2xl image-hover w-full aspect-square object-cover"
              />
              <img
                src={images.office4}
                alt="Private Office 4"
                className="rounded-2xl image-hover w-full aspect-square object-cover mt-8"
              />
            </motion.div>

            {/* Details */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-serif text-3xl font-bold text-brand mb-6">
                Office Space in Satellite Beach & Indialantic
              </h2>
              <p className="font-sans text-muted-foreground leading-relaxed mb-8">
                Our 16 private offices offer the perfect balance of professionalism and relaxation. 
                Located on A1A in Satellite Beach, we serve businesses from Indialantic, Melbourne Beach, 
                and throughout Brevard County Florida. Each office is fully furnished with ocean views, 
                natural lighting, and all the amenities of our coworking space.
              </p>

              <h3 className="font-serif text-xl font-semibold text-brand mb-4">
                What's Included
              </h3>
              <ul className="space-y-3 mb-8">
                {benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-sea flex-shrink-0 mt-0.5" strokeWidth={2} />
                    <span className="font-sans text-sm text-muted-foreground">{benefit}</span>
                  </li>
                ))}
              </ul>

              <div className="bg-sand-light/50 rounded-2xl p-8 mb-8">
                <h3 className="font-serif text-xl font-semibold text-brand mb-2">
                  Private Offices
                </h3>
                <p className="font-sans text-sm text-muted-foreground mb-4">
                  16 private offices: contact us for any current availability.
                </p>
                <Link to="/contact">
                  <Button 
                    className="bg-brand text-white hover:bg-brand-light rounded-full px-6 btn-primary"
                    data-testid="offices-inquiry-button"
                  >
                    Inquire About Offices
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-sand-light/30" data-testid="offices-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeUp}>
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-brand mb-4">
              Ready for Your Own Office?
            </h2>
            <p className="font-sans text-lg text-muted-foreground leading-relaxed mb-8">
              Schedule a tour to see our available private offices and find the perfect space for your business.
            </p>
            <Link to="/contact">
              <Button 
                size="lg" 
                className="bg-brand text-white hover:bg-brand-light rounded-full px-8 btn-primary"
                data-testid="offices-cta-button"
              >
                Request a Tour
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default OfficesPage;
